import dynamic from 'next/dynamic';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DragEvent, Ref } from 'react';
import type { ComposerProductInput } from '@vellnes/composer-react';
// Not yet re-exported by @vellnes/composer-react — see the same note in
// ComposerEmbed.tsx.
import type { InstanceLoadError } from '@vellnes/composer-core';

import { IconsEnum } from '../../../../assets/icons/types';

import { CheckPermissions } from '../../../../helpers/CheckPermissions';
import { PureTooltipIconButtonHelper } from '../../../../helpers/buttons/PureTooltipIconButtonHelper';
import { Translate } from '../../../../helpers/Translate';

import { composerKeys, words } from '../../../../locales/keys';
import { useTheme } from '../../../../app/hooks/useTheme';
import { useToastNotification } from '../../../../common/hooks/useToastNotification';
import { useTranslate } from '../../../../common/hooks/useTranslate';

import { useComposerRenderGeneration } from '../../hooks/useComposerRenderGeneration';
import { useComposerLifestyleGeneration } from '../../hooks/useComposerLifestyleGeneration';
import { useComposerScenePersistence } from '../../hooks/useComposerScenePersistence';
import {
  useComposerBackground,
  resolveComposerBackgroundInitialState
} from '../../hooks/useComposerBackground';
import { useComposerBackgroundControls } from '../../hooks/useComposerBackgroundControls';
import { useComposerDisplaySettings } from '../../hooks/useComposerDisplaySettings';
import { useComposerAspectRatio } from '../../hooks/useComposerAspectRatio';
import { ComposerRenderSidebar } from '../ComposerRenderSidebar';
import { ComposerRenderResultOverlay } from '../ComposerRenderResultOverlay';
import { ComposerBackgroundButton } from '../ComposerBackgroundButton';
import { ComposerDisplaySettingsButton } from '../ComposerDisplaySettingsButton';
import { ComposerAspectRatioButton } from '../ComposerAspectRatioButton';
import { ComposerAspectRatioFrame } from '../ComposerAspectRatioFrame';
import { ComposerProductsDock } from '../ComposerProductsDock';
import { ModelViewerColorPickerPopup } from '../../../modelViewers/components/content/ModelViewerEditor/components/ModelViewerColorPickerPopup';
import { ModelViewerLifestylePromptCard } from '../../../modelViewers/components/content/ModelViewerEditor/components/ModelViewerLifestylePromptCard';

import type { ComposerDockProductItemProduct } from '../ComposerDockProductItem';

import { ComposerPermissions } from '../../composerConstants';
import { RenderGenerationStatus } from '../../../renderGenerations/renderGenerationsTypes';
import { LifestyleGenerationStatus } from '../../../lifestyleGenerations/lifestyleGenerationsTypes';
import { getComposerThemeConfig } from '../../utils/getComposerThemeConfig';
import { TeamNanoID, TeamUUID } from '../../../teams/teamsTypes';

import type {
  ComposerEmbedHandle,
  ComposerEmbedProps
} from '../../ComposerEmbed';

// The composer widget runs WebGL + its own React root inside a Shadow DOM,
// so the embed must stay out of SSR.
//
// next/dynamic's LoadableComponent is a plain function component, not
// forwardRef — a `ref` prop passed straight through it is silently dropped by
// React. Pass the ref as a regular prop instead and let ComposerEmbed attach
// it itself.
const ComposerEmbed = dynamic<
  ComposerEmbedProps & { composerRef?: Ref<ComposerEmbedHandle> }
>(
  () =>
    import('../../ComposerEmbed').then((composerEmbed) => {
      function ComposerEmbedDynamic({
        composerRef,
        ...props
      }: ComposerEmbedProps & {
        composerRef?: Ref<ComposerEmbedHandle>;
      }) {
        return <composerEmbed.ComposerEmbed ref={composerRef} {...props} />;
      }
      return ComposerEmbedDynamic;
    }),
  {
    ssr: false,
    loading: () => (
      <div className="flex h-full w-full items-center justify-center text-sm text-gray-500 dark:text-gray-400">
        <Translate id={composerKeys.loading} />
      </div>
    )
  }
);

interface ComposerModalContentProps {
  products: ComposerProductInput[];
  companyNanoId?: TeamNanoID;
  companyUuid?: TeamUUID;
}

function ComposerModalContent({
  products,
  companyNanoId,
  companyUuid
}: ComposerModalContentProps) {
  const composerRef = useRef<ComposerEmbedHandle>(null);
  const { theme } = useTheme();
  const themeConfig = useMemo(() => getComposerThemeConfig(theme), [theme]);
  const { showToastNotification } = useToastNotification();
  const { t } = useTranslate();

  // Every sourceProductId handed to the widget (initial products + dock
  // additions) is tracked back to its product name, so a load-error toast
  // can name the product instead of just showing its raw source id.
  const productNameBySourceProductIdRef = useRef<Map<string, string>>(
    new Map(products.map((product) => [product.id, product.name]))
  );
  const {
    initialScene,
    initialRenderGenerations,
    initialDisplaySettings,
    handleSceneChange,
    handlePersistRenderGenerations,
    handlePersistDisplaySettings
  } = useComposerScenePersistence({ products });

  const { showDimensions, units, toggleShowDimensions, setUnits } =
    useComposerDisplaySettings(
      initialDisplaySettings && {
        showDimensions: initialDisplaySettings.showDimensions,
        units: initialDisplaySettings.units
      }
    );

  const {
    aspectRatio,
    customWidth,
    customHeight,
    cssValue: aspectRatioCssValue,
    requestedWidth,
    requestedHeight,
    setAspectRatio,
    setCustomWidth,
    setCustomHeight,
    setRequestedWidth,
    setRequestedHeight
  } = useComposerAspectRatio({
    composerRef,
    initialState: initialDisplaySettings && {
      aspectRatio: initialDisplaySettings.aspectRatio,
      customWidth: initialDisplaySettings.customWidth,
      customHeight: initialDisplaySettings.customHeight,
      requestedWidth: initialDisplaySettings.requestedWidth,
      requestedHeight: initialDisplaySettings.requestedHeight
    }
  });

  useEffect(() => {
    handlePersistDisplaySettings({
      showDimensions,
      units,
      aspectRatio,
      customWidth,
      customHeight,
      requestedWidth,
      requestedHeight
    });
  }, [
    showDimensions,
    units,
    aspectRatio,
    customWidth,
    customHeight,
    requestedWidth,
    requestedHeight,
    handlePersistDisplaySettings
  ]);

  const {
    renderGenerations,
    isRequesting,
    activeViewId,
    setActiveViewId,
    handleRequestRender,
    handleRemoveRenderGeneration,
    handleStopRenderGeneration
  } = useComposerRenderGeneration({
    composerRef,
    initialRenderGenerations,
    onPersist: handlePersistRenderGenerations,
    requestedWidth,
    requestedHeight
  });

  // Subject for lifestyle generation when the overlay shows a finished
  // render. When it shows a finished lifestyle, the hook falls back to that
  // lifestyle's own result (it owns the lifestyle list).
  const activeRenderResultUrl = useMemo(
    () =>
      renderGenerations.find((g) => g.localId === activeViewId)
        ?.resultFullUrl ?? null,
    [renderGenerations, activeViewId]
  );

  const {
    lifestyleGenerations,
    prompt: lifestylePrompt,
    setPrompt: setLifestylePrompt,
    referenceFiles,
    isUploadingReference,
    isGenerating: isGeneratingLifestyle,
    generateError: lifestyleGenerateError,
    handleReferenceFilesChange,
    handleReferenceFileDrop,
    handleRemoveReference,
    handleComposerGenerate,
    handleRemoveLifestyleGeneration,
    handleStopLifestyleGeneration
  } = useComposerLifestyleGeneration({
    activeViewId,
    setActiveViewId,
    subjectImageUrl: activeRenderResultUrl
  });

  // Renders and generated lifestyles share the sidebar and the overlay view
  // switcher — newest first, regardless of kind.
  const generations = useMemo(
    () =>
      [...renderGenerations, ...lifestyleGenerations].sort(
        (a, b) => b.createdOrder - a.createdOrder
      ),
    [renderGenerations, lifestyleGenerations]
  );

  const handleRemoveGeneration = useCallback(
    (localId: string) => {
      const item = generations.find((g) => g.localId === localId);
      if (item?.kind === 'lifestyle') {
        handleRemoveLifestyleGeneration(localId);
      } else {
        handleRemoveRenderGeneration(localId);
      }
    },
    [generations, handleRemoveLifestyleGeneration, handleRemoveRenderGeneration]
  );

  const handleStopGeneration = useCallback(
    (localId: string) => {
      const item = generations.find((g) => g.localId === localId);
      if (item?.kind === 'lifestyle') {
        handleStopLifestyleGeneration(localId);
      } else {
        handleStopRenderGeneration(localId);
      }
    },
    [generations, handleStopLifestyleGeneration, handleStopRenderGeneration]
  );

  // Only computed for the initial render — useState ignores it on re-renders,
  // and the scene this reads from is the one loaded when Compose opened.
  const backgroundInitialState = useMemo(
    () =>
      resolveComposerBackgroundInitialState(
        initialScene?.background,
        themeConfig.sceneBackgroundColor
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const {
    backgroundMode,
    customColor,
    backgroundColor,
    setPresetBackground,
    setCustomColor
  } = useComposerBackground(
    themeConfig.sceneBackgroundColor,
    backgroundInitialState
  );

  const {
    colorPickerOpen,
    colorPickerPrevColor,
    handleOpenColorPicker,
    handleConfirmColorPicker,
    handleCancelColorPicker
  } = useComposerBackgroundControls({
    backgroundMode,
    customColor,
    setCustomColor,
    setPresetBackground
  });

  useEffect(() => {
    composerRef.current?.setBackground(
      backgroundMode === 'default'
        ? null
        : { mode: 'color', color: backgroundColor }
    );
  }, [backgroundMode, backgroundColor]);

  const activeGeneration = useMemo(
    () =>
      activeViewId !== 'scene'
        ? generations.find((g) => g.localId === activeViewId) ?? null
        : null,
    [activeViewId, generations]
  );

  const isActiveGenerationLoading =
    activeGeneration?.kind === 'lifestyle'
      ? activeGeneration.status === LifestyleGenerationStatus.Pending ||
        activeGeneration.status === LifestyleGenerationStatus.Processing ||
        activeGeneration.status === LifestyleGenerationStatus.PreparingSource
      : activeGeneration?.status === RenderGenerationStatus.Pending ||
        activeGeneration?.status === RenderGenerationStatus.Processing;

  // The widget's WebGL canvas (composer-core's own Shadow DOM root) can
  // paint its dimension-label overlay through an opaque z-index sibling —
  // GPU-composited canvas layers aren't reliably constrained by CSS stacking
  // order the way regular DOM content is. Forcing dimensions off narrows the
  // window; visually hiding the whole viewport below (visibility, not
  // unmount — keeps the WebGL context and loaded models alive) is what
  // actually stops the browser from compositing it on screen at all.
  const isShowingRenderResult = Boolean(activeGeneration?.resultFullUrl);
  const widgetShowDimensions = showDimensions && !isShowingRenderResult;

  // The product being dragged out of the dock. Held here (not in the dock) so
  // the viewport's drop handler can consume it. `isDragOverViewport` only drives
  // the drop-hint overlay visibility.
  const [draggedProduct, setDraggedProduct] =
    useState<ComposerDockProductItemProduct | null>(null);
  const [isDragOverViewport, setIsDragOverViewport] = useState(false);

  const handleAddProductToScene = useCallback(
    (product: ComposerDockProductItemProduct) => {
      const glbUrl = product.previewModel?.file;
      if (!glbUrl) return;
      // ComposerProductInput.id lands in the widget's `sourceProductId`, not
      // its `instanceId` (the widget mints instanceId itself). A fresh
      // per-add suffix here only keeps sourceProductId distinguishable when
      // the same product is added more than once (e.g. for the load-error
      // toast lookup below) — it does NOT protect against instanceId
      // collisions; that's a composer-core bug tracked in
      // vymir-studio/docs/tasks/composer-core-instance-id-collision-on-loaded-scene.md.
      const sourceProductId = `${product.id}-${Math.random()
        .toString(36)
        .slice(2, 9)}`;
      productNameBySourceProductIdRef.current.set(
        sourceProductId,
        product.name
      );
      composerRef.current?.addProduct({
        id: sourceProductId,
        name: product.name,
        glbUrl
      });
    },
    []
  );

  const handleLoadError = useCallback(
    (error: InstanceLoadError) => {
      console.error('[Composer] failed to load product model', error);
      const productName =
        productNameBySourceProductIdRef.current.get(error.sourceProductId) ??
        error.sourceProductId;
      showToastNotification(
        t(composerKeys.productLoadFailed, { productName }),
        'error'
      );
    },
    [showToastNotification, t]
  );

  const handleViewportDragOver = useCallback(
    (e: DragEvent) => {
      if (!draggedProduct) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      setIsDragOverViewport(true);
    },
    [draggedProduct]
  );

  const handleViewportDragLeave = useCallback((e: DragEvent) => {
    // Ignore drag-leave bubbling from child elements — only reset the hint when
    // the cursor actually leaves the viewport container.
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    setIsDragOverViewport(false);
  }, []);

  const handleViewportDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      setIsDragOverViewport(false);
      if (draggedProduct) {
        handleAddProductToScene(draggedProduct);
      }
      setDraggedProduct(null);
    },
    [draggedProduct, handleAddProductToScene]
  );

  return (
    <div className="flex h-full overflow-hidden">
      <div className="relative flex-1 flex flex-col overflow-hidden">
        <div
          className="relative flex-1 min-h-0 overflow-hidden"
          onDragOver={handleViewportDragOver}
          onDragLeave={handleViewportDragLeave}
          onDrop={handleViewportDrop}
        >
          <div className={isShowingRenderResult ? 'invisible' : undefined}>
            <ComposerAspectRatioFrame
              aspectRatio={aspectRatio}
              cssValue={aspectRatioCssValue}
            >
              <ComposerEmbed
                composerRef={composerRef}
                initialProducts={products}
                initialScene={initialScene}
                themeConfig={themeConfig}
                showDimensions={widgetShowDimensions}
                dimensionUnits={units}
                onSceneChange={handleSceneChange}
                onLoadError={handleLoadError}
              />
            </ComposerAspectRatioFrame>
          </div>

          <CheckPermissions
            action={ComposerPermissions.READ_COMPOSER_RENDER_GENERATOR}
          >
            {activeGeneration?.resultFullUrl && (
              <ComposerRenderResultOverlay
                resultFullUrl={activeGeneration.resultFullUrl}
                downloadType={
                  activeGeneration.kind === 'lifestyle'
                    ? 'lifestyle'
                    : 'composite-render'
                }
                onBack={() => setActiveViewId('scene')}
              >
                <ModelViewerLifestylePromptCard
                  prompt={lifestylePrompt}
                  references={referenceFiles}
                  allowMultipleReferences
                  isUploadingReference={isUploadingReference}
                  isGenerating={isGeneratingLifestyle}
                  generateError={lifestyleGenerateError}
                  onPromptChange={setLifestylePrompt}
                  onReferenceFileChange={handleReferenceFilesChange}
                  onReferenceFileDrop={handleReferenceFileDrop}
                  onRemoveReference={handleRemoveReference}
                  onGenerate={handleComposerGenerate}
                  i18nPromptPlaceholder={
                    composerKeys.lifestylePromptPlaceholder
                  }
                  i18nGenerateText={words.generate}
                  i18nAddReferenceText={composerKeys.addReferences}
                />
              </ComposerRenderResultOverlay>
            )}

            {activeGeneration && isActiveGenerationLoading && (
              <div className="absolute inset-0 z-30 bg-white dark:bg-gray-900 flex items-center justify-center flex-col gap-4">
                <div className="animate-spin w-16 h-16 rounded-full border-4 border-gray-200 dark:border-gray-700 border-t-blue-600 dark:border-t-blue-600" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  <Translate
                    id={
                      activeGeneration.kind === 'lifestyle'
                        ? composerKeys.generatingLifestyle
                        : composerKeys.generatingRender
                    }
                  />
                </p>
                <p className="max-w-sm text-center text-xs leading-relaxed text-gray-400 dark:text-gray-500">
                  <Translate
                    id={
                      activeGeneration.kind === 'lifestyle'
                        ? composerKeys.generatingLifestyleMessage
                        : composerKeys.generatingRenderMessage
                    }
                  />
                </p>
              </div>
            )}
          </CheckPermissions>

          <div className="absolute bottom-4 left-4 z-20 flex items-center gap-1">
            <ComposerBackgroundButton
              backgroundMode={backgroundMode}
              customColor={customColor}
              onSelectPreset={setPresetBackground}
              onSelectCustom={handleOpenColorPicker}
            />
            <ComposerDisplaySettingsButton
              showDimensions={showDimensions}
              onToggleDimensions={toggleShowDimensions}
              units={units}
              onSelectUnits={setUnits}
            />
            <ComposerAspectRatioButton
              aspectRatio={aspectRatio}
              onSelectAspectRatio={setAspectRatio}
              customWidth={customWidth}
              customHeight={customHeight}
              onCustomWidthChange={setCustomWidth}
              onCustomHeightChange={setCustomHeight}
              requestedWidth={requestedWidth}
              requestedHeight={requestedHeight}
              onRequestedWidthChange={setRequestedWidth}
              onRequestedHeightChange={setRequestedHeight}
            />
          </div>

          <div className="absolute bottom-4 right-4 z-20 flex flex-col items-end gap-2">
            <CheckPermissions
              action={ComposerPermissions.READ_COMPOSER_RENDER_GENERATOR}
            >
              <PureTooltipIconButtonHelper
                icon={IconsEnum.PHOTOGRAPH_SOLID}
                iconClassName="h-5 w-5"
                i18nText={composerKeys.studioRender}
                i18nTextClassName="ml-1.5"
                className="py-3 pl-3 pr-6 space-x-1 rounded-full inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base text-white bg-blue-500 hover:bg-blue-600 shadow-sm hover:shadow-md transition-colors duration-150 ease-out disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={isRequesting}
                onClick={handleRequestRender}
                tooltipI18nText={
                  isRequesting
                    ? composerKeys.generatingRender
                    : composerKeys.requestRender
                }
              />
            </CheckPermissions>
          </div>

          {colorPickerOpen && (
            <ModelViewerColorPickerPopup
              isOpen={colorPickerOpen}
              initialColor={colorPickerPrevColor}
              onChangePreview={setCustomColor}
              onConfirm={handleConfirmColorPicker}
              onCancel={handleCancelColorPicker}
            />
          )}

          {draggedProduct && isDragOverViewport && (
            <div className="absolute inset-3 z-10 flex items-center justify-center rounded-xl border-2 border-dashed border-blue-500 bg-blue-500/10 pointer-events-none">
              <span className="rounded-full bg-blue-500 px-4 py-2 text-sm font-medium text-white shadow-sm">
                <Translate id={composerKeys.releaseToPlace} />
              </span>
            </div>
          )}
        </div>

        <ComposerProductsDock
          companyNanoId={companyNanoId}
          companyUuid={companyUuid}
          onAddProductToScene={handleAddProductToScene}
          onDraggedProductChange={setDraggedProduct}
        />
      </div>

      <CheckPermissions
        action={ComposerPermissions.READ_COMPOSER_RENDER_GENERATOR}
      >
        {generations.length > 0 && (
          <ComposerRenderSidebar
            generations={generations}
            activeViewId={activeViewId}
            onSelectView={setActiveViewId}
            onRemoveRender={handleRemoveGeneration}
            onStopRender={handleStopGeneration}
          />
        )}
      </CheckPermissions>
    </div>
  );
}

export default ComposerModalContent;
